/**
 * FrontLot — PDF fill engine
 *
 * Runs in the browser. Templates are fetched from Supabase Storage, filled
 * with pdf-lib, flattened, and merged into one document for printing.
 * No server compute, so no per-document cost.
 *
 * The approach here was validated against the real HSMV and FTC PDFs before
 * being written: field names all resolve, XFA stripping works, and the
 * Buyers Guide page selection produces the right two pages.
 */

import {
  rgb,
  PDFDocument,
  type PDFFont,
  type PDFPage,
  PDFName,
  PDFRef,
  PDFDict,
  PDFTextField,
  PDFCheckBox,
  PDFRadioGroup,
  PDFDropdown,
  StandardFonts,
} from "pdf-lib";

import { stampSignatures, type Marks, type StampReport } from "../lib/signing/stamp";

import {
  FORMS,
  RETAIL_PACKET,
  type FormContext,
  type FormSpec,
  type FieldValues,
} from "./mappings";

export interface FillReport {
  formKey: string;
  fieldsSet: number;
  /** Names in the mapping that don't exist in the template. Should be empty. */
  unresolved: string[];
  /** Fields present but not settable, e.g. wrong export value. */
  failed: { name: string; reason: string }[];
  /** Values shortened to fit. Worth a look, not worth stopping for. */
  clipped: { name: string; from: string; to: string }[];
  /** Where signatures landed, and any anchor this template did not have.
      Null when the document was produced unsigned. */
  stamped?: StampReport | null;
}

export class FormFillError extends Error {
  constructor(message: string, public readonly report?: FillReport) {
    super(message);
    this.name = "FormFillError";
  }
}

// ---------------------------------------------------------------------------
// Template loading
// ---------------------------------------------------------------------------

type TemplateLoader = (path: string) => Promise<ArrayBuffer>;

/** Default loader: templates are static files served from public/forms. */
export const fetchFromPublic: TemplateLoader = async (path) => {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Missing form template: ${path}`);
  return res.arrayBuffer();
};

/** In-memory cache — templates never change within a session. */
const templateCache = new Map<string, ArrayBuffer>();

export async function loadTemplate(
  path: string,
  fetchTemplate: TemplateLoader,
): Promise<ArrayBuffer> {
  const hit = templateCache.get(path);
  if (hit) return hit;
  const bytes = await fetchTemplate(path);
  templateCache.set(path, bytes);
  return bytes;
}

// ---------------------------------------------------------------------------
// XFA
// ---------------------------------------------------------------------------

/**
 * Remove the XFA layer from a hybrid form.
 *
 * The FTC Buyers Guide carries both an AcroForm widget layer and an XFA layer.
 * Acrobat prefers XFA, so a document filled only at the widget level can open
 * completely blank even though the values are stored correctly. Deleting /XFA
 * forces every viewer onto the widgets we populated.
 */
function stripXfa(doc: PDFDocument): boolean {
  const acro = doc.catalog.lookupMaybe(PDFName.of("AcroForm"), PDFDict);
  if (!acro) return false;
  if (!acro.has(PDFName.of("XFA"))) return false;
  acro.delete(PDFName.of("XFA"));
  return true;
}

// ---------------------------------------------------------------------------
// Field setting
// ---------------------------------------------------------------------------

/**
 * Values starting with "/" are button states, e.g. "/On", "/YES_4", "/As Is".
 * Everything else is text.
 */
function isButtonState(v: string): boolean {
  return v.startsWith("/");
}

/** Split text into lines that fit a given width, using real font metrics. */
function wrapLines(text: string, font: PDFFont, size: number, usable: number): string[] {
  const fits = (t: string) => font.widthOfTextAtSize(t, size) <= usable;
  const out: string[] = [];
  for (const para of text.split("\n")) {
    if (para === "") { out.push(""); continue; }
    let line = "";
    for (const word of para.split(" ")) {
      const candidate = line ? `${line} ${word}` : word;
      if (fits(candidate)) { line = candidate; continue; }
      if (line) out.push(line);
      if (fits(word)) { line = word; continue; }
      let chunk = "";
      for (const ch of word) {
        if (fits(chunk + ch)) chunk += ch;
        else { out.push(chunk); chunk = ch; }
      }
      line = chunk;
    }
    out.push(line);
  }
  return out;
}

/**
 * Draw a long value onto the page inside the field's own rectangle, then
 * blank the field.
 *
 * pdf-lib will not reliably re-flow a multiline field's appearance, so a long
 * value comes out as a single clipped line. Drawing the text ourselves does
 * not depend on the library or the viewer choosing to wrap it. The field is
 * emptied so nothing renders twice.
 */
function drawIntoField(
  doc: PDFDocument,
  field: PDFTextField,
  text: string,
  font: PDFFont,
  size: number,
): boolean {
  const widget = field.acroField.getWidgets()[0];
  if (!widget) return false;

  const rect = widget.getRectangle();
  const pageRef = widget.P();
  const page: PDFPage | undefined = pageRef instanceof PDFRef
    ? doc.getPages().find((pg) => pg.ref === pageRef)
    : doc.getPages()[0];
  if (!page) return false;

  const padX = 4;
  const usable = rect.width - padX * 2;
  if (usable <= 0) return false;

  const lines = wrapLines(text, font, size, usable);

  // If it overflows the box, step the size down rather than run off the page.
  let s = size;
  let laid = lines;
  while (laid.length * (s * 1.16) > rect.height - 8 && s > 5.5) {
    s -= 0.5;
    laid = wrapLines(text, font, s, rect.width - padX * 2);
  }

  let y = rect.y + rect.height - (s * 1.16);
  for (const line of laid) {
    if (y < rect.y + 2) break;
    if (line) {
      page.drawText(line, {
        x: rect.x + padX, y, size: s, font,
        color: rgb(0.08, 0.10, 0.12),
      });
    }
    y -= s * 1.16;
  }

  field.setText("");
  return true;
}


interface ApplyResult {
  set: number;
  unresolved: string[];
  failed: { name: string; reason: string }[];
  /** Values shortened to fit a field's maximum length. */
  clipped: { name: string; from: string; to: string }[];
}

function applyValues(
  doc: PDFDocument,
  values: FieldValues,
  font?: PDFFont,
  multilineSize = 8,
): ApplyResult {
  const form = doc.getForm();
  const present = new Set(form.getFields().map((f) => f.getName()));

  const unresolved: string[] = [];
  const failed: { name: string; reason: string }[] = [];
  const clipped: { name: string; from: string; to: string }[] = [];
  let set = 0;

  for (const [name, raw] of Object.entries(values)) {
    if (!present.has(name)) {
      unresolved.push(name);
      continue;
    }
    // Empty string means "leave blank on purpose" — skip without counting.
    if (raw === "") continue;

    try {
      const field = form.getField(name);

      if (field instanceof PDFTextField) {
        // Some state fields are tightly capped (Make and Color are six
        // characters on 82993). Clip rather than throw: a slightly short
        // value is recoverable by hand, a failed packet is not.
        const max = field.getMaxLength();
        let value = typeof max === "number" && max > 0 && raw.length > max
          ? raw.slice(0, max)
          : raw;
        if (value !== raw) clipped.push({ name, from: raw, to: value });

        // Long or multiline values are drawn onto the page instead of being
        // handed to the field, because field re-flow cannot be relied on.
        if (font && (field.isMultiline() || value.includes("\n"))) {
          if (drawIntoField(doc, field, value, font, multilineSize)) {
            set++;
            continue;
          }
        }
        field.setText(value);
        set++;
      } else if (field instanceof PDFCheckBox) {
        // "/Off" is the default state, so there is nothing to do and nothing
        // that can fail. Only ticking a box is an action.
        if (!(isButtonState(raw) && raw.toLowerCase() === "/off")) {
          field.check();
          set++;
        }
      } else if (field instanceof PDFRadioGroup) {
        const option = isButtonState(raw) ? raw.slice(1) : raw;
        const allowed = field.getOptions();
        if (!allowed.includes(option)) {
          failed.push({
            name,
            reason: `export value "${option}" not in [${allowed.join(", ")}]`,
          });
          continue;
        }
        field.select(option);
        set++;
      } else if (field instanceof PDFDropdown) {
        field.select(raw);
        set++;
      } else {
        failed.push({ name, reason: `unsupported field type` });
      }
    } catch (e) {
      failed.push({ name, reason: e instanceof Error ? e.message : String(e) });
    }
  }

  return { set, unresolved, failed, clipped };
}

// ---------------------------------------------------------------------------
// Single form
// ---------------------------------------------------------------------------

export interface FilledForm {
  spec: FormSpec;
  doc: PDFDocument;
  report: FillReport;
}

export async function fillForm(
  spec: FormSpec,
  ctx: FormContext,
  fetchTemplate: TemplateLoader,
  opts: { flatten?: boolean; marks?: Marks; signedOn?: string } = {},
): Promise<FilledForm> {
  const bytes = await loadTemplate(spec.templatePath, fetchTemplate);

  const doc = await PDFDocument.load(bytes);

  if (spec.hasXfa) stripXfa(doc);

  let set = 0;
  let stamped: StampReport | null = null;
  let unresolved: string[] = [];
  let failed: { name: string; reason: string }[] = [];
  let clipped: { name: string; from: string; to: string }[] = [];

  if (spec.fillMethod === "acroform" && spec.map) {
    const values = spec.map(ctx);
    // Embed the font first so wrapping can measure with the real metrics.
    const helv = await doc.embedFont(StandardFonts.Helvetica);
    const r = applyValues(doc, values, helv, spec.multilineSize ?? 8);
    set = r.set;
    unresolved = r.unresolved;
    failed = r.failed;
    clipped = r.clipped;

    const form = doc.getForm();
    // Generate appearance streams ourselves rather than relying on the viewer.
    try {
      form.updateFieldAppearances(helv);
    } catch {
      // Some templates carry their own DA strings; not fatal.
    }
    /* Signatures go on before flattening, because the anchor rectangles are
       read from the form fields and flattening destroys them. */
    if (opts.marks) {
      stamped = await stampSignatures(doc, spec.key, opts.marks);
    }

    // Flatten so the printed document can't be edited after signing.
    if (opts.flatten !== false) {
      try {
        form.flatten();
      } catch (e) {
        throw new FormFillError(
          `Could not flatten ${spec.key}: ${e instanceof Error ? e.message : e}`,
        );
      }
    }
  }

  // Drop pages that don't belong in the packet, e.g. the Buyers Guide's
  // "implied warranties" variant, which contradicts an as-is sale.
  if (spec.keepPages) {
    const keep = new Set(spec.keepPages);
    for (let i = doc.getPageCount() - 1; i >= 0; i--) {
      if (!keep.has(i)) doc.removePage(i);
    }
  }

  const report: FillReport = {
    formKey: spec.key,
    fieldsSet: set,
    unresolved,
    failed,
    clipped,
    stamped,
  };
  if (clipped.length) {
    console.warn(`${spec.title}: shortened to fit —`,
      clipped.map((c) => `${c.name}: "${c.from}" → "${c.to}"`).join("; "));
  }

  if (unresolved.length || failed.length) {
    // Loud by design: a silently half-filled legal form is worse than an
    // error. But the message has to name the field, or it is just noise.
    const details = [
      ...unresolved.map((n) => `no such field "${n}"`),
      ...failed.map((f) => `"${f.name}" — ${f.reason}`),
    ].join("; ");
    throw new FormFillError(`${spec.title}: ${details}`, report);
  }

  return { spec, doc, report };
}

// ---------------------------------------------------------------------------
// Packet
// ---------------------------------------------------------------------------

export interface PacketResult {
  bytes: Uint8Array;
  reports: FillReport[];
  pageCount: number;
}

/**
 * Build the merged retail packet: one PDF, one print job to the wireless
 * printer, in the order the deal jacket wants it.
 */
export async function buildRetailPacket(
  ctx: FormContext,
  fetchTemplate: TemplateLoader,
  formKeys: string[] = RETAIL_PACKET,
  /** Supply to produce a signed packet. Omit for a blank one to sign by hand. */
  signing?: { marks: Marks; signedOn: string },
): Promise<PacketResult> {
  const out = await PDFDocument.create();
  const reports: FillReport[] = [];

  for (const key of formKeys) {
    const spec = FORMS.find((f) => f.key === key);
    if (!spec) throw new FormFillError(`Unknown form "${key}"`);

    const filled = await fillForm(spec, ctx, fetchTemplate,
      signing ? { marks: signing.marks, signedOn: signing.signedOn } : {});
    reports.push(filled.report);

    const pages = await out.copyPages(
      filled.doc,
      filled.doc.getPageIndices(),
    );
    pages.forEach((p) => out.addPage(p));
  }

  out.setTitle(`Deal ${ctx.deal.dealNo} — ${ctx.vehicle.year} ${ctx.vehicle.make} ${ctx.vehicle.model}`);
  out.setProducer("FrontLot");
  out.setCreationDate(new Date());

  return {
    bytes: await out.save(),
    reports,
    pageCount: out.getPageCount(),
  };
}

/** Fill and return a single form, e.g. the Buyers Guide from a vehicle record. */
export async function buildSingleForm(
  formKey: string,
  ctx: FormContext,
  fetchTemplate: TemplateLoader,
  signing?: { marks: Marks; signedOn: string },
): Promise<PacketResult> {
  return buildRetailPacket(ctx, fetchTemplate, [formKey], signing);
}
